import { Box, Typography } from "@mui/material";
import { Cancel, CheckCircle, ErrorOutline } from "@mui/icons-material";
import { V2_COLORS } from "../constants/colors";
import { getFieldLabel } from "../constants/fieldLabels";
import type { CheckValue, ValidationCheck } from "../types/action";

// Multi-value checks (hsn_code) send arrays; React would render them with no
// separator ("8302109039221000…"), so join the non-blank entries with ", ".
export function formatCheckValue(value: CheckValue | undefined): string {
  if (Array.isArray(value)) {
    return value.filter((v) => v != null && String(v).trim() !== "").join(", ") || "\u2014";
  }
  return value || "\u2014";
}

// --- Row components ---

function PassRow({ check }: { check: ValidationCheck }) {
  // Most checks are labelled by the invoice attribute they validate, so the
  // invoice-side value is the right thing to show. `po_date` is the exception:
  // its label refers to the PO's date (system side), while `invoice_value`
  // holds the invoice date \u2014 showing that would mislabel the invoice date as
  // the PO date (ENG-25278). Display the system value for it.
  const displayValue =
    check.field === "po_date" ? check.system_value : check.invoice_value;
  return (
    <Box display="flex" alignItems="center" gap={1.5} py={1.25}>
      <CheckCircle sx={{ fontSize: 20, color: V2_COLORS.success, flexShrink: 0 }} />
      <Typography fontSize="13px" fontWeight={600} sx={{ minWidth: 120, flexShrink: 0 }}>
        {getFieldLabel(check.field)}
      </Typography>
      <Typography fontSize="13px" color={V2_COLORS.textSecondary} sx={{ minWidth: 0 }}>
        {formatCheckValue(displayValue)}
      </Typography>
    </Box>
  );
}

function FailRow({ check }: { check: ValidationCheck }) {
  return (
    <Box py={1.25}>
      <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
        <Cancel sx={{ fontSize: 20, color: V2_COLORS.error, flexShrink: 0 }} />
        <Typography fontSize="13px" fontWeight={600}>
          {getFieldLabel(check.field)}
        </Typography>
      </Box>
      <Box pl={4.5} display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" gap={1}>
          <Typography fontSize="12px" color={V2_COLORS.textSecondary} sx={{ minWidth: 72, flexShrink: 0 }}>
            Expected:
          </Typography>
          <Typography fontSize="12px">{formatCheckValue(check.system_value)}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Typography fontSize="12px" color={V2_COLORS.textSecondary} sx={{ minWidth: 72, flexShrink: 0 }}>
            Found:
          </Typography>
          <Typography fontSize="12px">{formatCheckValue(check.invoice_value)}</Typography>
        </Box>
        {check.reason && (
          <Box display="flex" gap={1}>
            <Typography fontSize="12px" color={V2_COLORS.textSecondary} sx={{ minWidth: 72, flexShrink: 0 }}>
              Reason:
            </Typography>
            <Typography fontSize="12px" color={V2_COLORS.error}>
              {check.reason}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// --- Main component ---

interface ValidationChecksCardProps {
  checks: ValidationCheck[];
}

export default function ValidationChecksCard({ checks }: ValidationChecksCardProps) {
  const total = checks.length;
  const passedCount = checks.filter((c) => c.match).length;
  const failedCount = total - passedCount;
  const allPassed = failedCount === 0;

  if (total === 0) {
    return (
      <Box>
        <Typography variant="h4" mb={1.5}>
          Validation Checks
        </Typography>
        <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", py: 3, px: 2 }}>
          <Typography color="textSecondary" textAlign="center">
            No validation checks available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={1.5}>
        Validation Checks
      </Typography>
      <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", py: 2, px: 2 }}>
        {/* Status banner */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          px={1.5}
          py={1}
          mb={2}
          sx={{
            bgcolor: allPassed ? V2_COLORS.successSoftBg : V2_COLORS.errorBg,
            borderRadius: "6px",
          }}
        >
          {allPassed ? (
            <CheckCircle sx={{ fontSize: 18, color: V2_COLORS.success }} />
          ) : (
            <ErrorOutline sx={{ fontSize: 18, color: V2_COLORS.error }} />
          )}
          <Typography
            fontSize="13px"
            fontWeight={600}
            color={allPassed ? V2_COLORS.successSoft : V2_COLORS.error}
          >
            {allPassed
              ? `All ${total} checks passed`
              : `${failedCount}/${total} field${failedCount > 1 ? "s" : ""} require manual review`}
          </Typography>
        </Box>

        {/* Check rows */}
        {checks.map((check, idx) => (
          <Box
            key={check.field}
            sx={{
              borderBottom: idx < checks.length - 1 ? `1px solid ${V2_COLORS.border}` : "none",
            }}
          >
            {check.match ? <PassRow check={check} /> : <FailRow check={check} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
